/**
 * Exercise Substitution System
 *
 * Provides safer alternatives when injury risk is elevated.
 * Maintains training stimulus while reducing injury risk.
 * Also provides SMART substitution recommendations based on muscle groups,
 * tracking type, and movement pattern.
 */

import { getExerciseHistory, type ExerciseLog } from './sessionStorage';
import { getRecoveryHistory } from './recoveryCalculator';
import { ALL_EXERCISES, findExercise, type Exercise, type MuscleGroup, type TrackingType } from './exerciseDatabase';

// ============================================================================
// TYPES
// ============================================================================

export interface SubstitutionResult {
  originalExercise: string;
  substituteExercise: string;
  reason: string;
  maintainsStimulus: boolean;
  injuryRiskReduction: 'high' | 'medium' | 'low';
}

// ============================================================================
// SUBSTITUTION MAPPINGS
// ============================================================================

/**
 * Maps exercises to safer alternatives that maintain training stimulus
 * Format: [original] -> [substitute, reason, maintains stimulus level]
 */
const SUBSTITUTION_MAP: Record<string, {
  substitute: string;
  reason: string;
  maintainsStimulus: boolean;
  injuryRiskReduction: 'high' | 'medium' | 'low';
}[]> = {
  // ===== SHOULDER PROTECTION =====
  'barbell overhead press': [
    { substitute: 'Dumbbell Shoulder Press', reason: 'More joint-friendly ROM', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Landmine Press', reason: 'Reduced shoulder impingement risk', maintainsStimulus: true, injuryRiskReduction: 'high' },
  ],
  'military press': [
    { substitute: 'Dumbbell Shoulder Press', reason: 'More joint-friendly ROM', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Arnold Press', reason: 'Natural rotation reduces impingement', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'upright row': [
    { substitute: 'Face Pulls', reason: 'Eliminates internal rotation impingement', maintainsStimulus: false, injuryRiskReduction: 'high' },
    { substitute: 'Lateral Raises', reason: 'Targets same muscles without impingement', maintainsStimulus: true, injuryRiskReduction: 'high' },
  ],
  
  // ===== BACK PROTECTION =====
  'barbell row': [
    { substitute: 'Chest Supported Row', reason: 'Removes lower back strain', maintainsStimulus: true, injuryRiskReduction: 'high' },
    { substitute: 'Seated Cable Row', reason: 'Stable position for back', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'deadlift': [
    { substitute: 'Trap Bar Deadlift', reason: 'More neutral spine position', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Romanian Deadlift', reason: 'Reduced lower back flexion', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Hip Thrust', reason: 'Glute focus without spinal load', maintainsStimulus: false, injuryRiskReduction: 'high' },
  ],
  'good morning': [
    { substitute: 'Romanian Deadlift', reason: 'Similar stimulus, less spinal shear', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Back Extension', reason: 'Bodyweight alternative', maintainsStimulus: false, injuryRiskReduction: 'high' },
  ],
  
  // ===== KNEE PROTECTION =====
  'back squat': [
    { substitute: 'Goblet Squat', reason: 'Promotes better knee tracking', maintainsStimulus: false, injuryRiskReduction: 'medium' },
    { substitute: 'Leg Press', reason: 'Removes spinal load', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Box Squat', reason: 'Controlled depth reduces knee stress', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'front squat': [
    { substitute: 'Goblet Squat', reason: 'Easier on wrists and shoulders', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Leg Press', reason: 'Similar quad emphasis', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'walking lunge': [
    { substitute: 'Reverse Lunge', reason: 'Better knee tracking', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Step-Up', reason: 'More controlled movement', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'leg extension': [
    { substitute: 'Spanish Squat', reason: 'Reduced patella stress', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Terminal Knee Extension', reason: 'PT-approved alternative', maintainsStimulus: false, injuryRiskReduction: 'high' },
  ],
  
  // ===== CHEST/SHOULDER PROTECTION =====
  'bench press': [
    { substitute: 'Dumbbell Bench Press', reason: 'More natural ROM for shoulders', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Floor Press', reason: 'Limited ROM protects shoulders', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'incline bench press': [
    { substitute: 'Incline Dumbbell Press', reason: 'Better shoulder positioning', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Landmine Press', reason: 'Shoulder-friendly pressing', maintainsStimulus: true, injuryRiskReduction: 'high' },
  ],
  'dips': [
    { substitute: 'Close Grip Bench Press', reason: 'Similar tricep stimulus, less shoulder stress', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Tricep Pushdown', reason: 'Isolation without shoulder involvement', maintainsStimulus: false, injuryRiskReduction: 'high' },
  ],
  
  // ===== ELBOW PROTECTION =====
  'skull crusher': [
    { substitute: 'Tricep Pushdown', reason: 'Less elbow stress', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Overhead Tricep Extension', reason: 'Different angle reduces strain', maintainsStimulus: true, injuryRiskReduction: 'medium' },
  ],
  'close grip bench press': [
    { substitute: 'Diamond Push-Up', reason: 'Bodyweight reduces absolute stress', maintainsStimulus: false, injuryRiskReduction: 'medium' },
    { substitute: 'Tricep Pushdown', reason: 'Isolation movement', maintainsStimulus: false, injuryRiskReduction: 'high' },
  ],
  
  // ===== WRIST PROTECTION =====
  'barbell curl': [
    { substitute: 'Dumbbell Curl', reason: 'Natural wrist rotation', maintainsStimulus: true, injuryRiskReduction: 'medium' },
    { substitute: 'Hammer Curl', reason: 'Neutral grip reduces strain', maintainsStimulus: true, injuryRiskReduction: 'high' },
  ],
  'push-ups': [
    { substitute: 'Push-Ups (Dumbbells)', reason: 'Neutral wrist position', maintainsStimulus: true, injuryRiskReduction: 'high' },
  ],
};

// ============================================================================
// MAIN FUNCTIONS
// ============================================================================

/**
 * Get a substitute exercise based on soreness and injury risk
 */
export function getExerciseSubstitute(
  exerciseName: string,
  injuryRisk: 'high' | 'medium' | 'low' = 'medium'
): SubstitutionResult | null {
  const normalizedName = exerciseName.toLowerCase().trim();
  
  // Check for exact match first
  let substitutes = SUBSTITUTION_MAP[normalizedName];
  
  // If no exact match, try partial match
  if (!substitutes) {
    for (const key in SUBSTITUTION_MAP) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        substitutes = SUBSTITUTION_MAP[key];
        break;
      }
    }
  }
  
  if (!substitutes || substitutes.length === 0) {
    return null;
  }
  
  // Select substitute based on injury risk level
  let selected = substitutes[0]; // Default to first
  
  if (injuryRisk === 'high') {
    // Prefer highest risk reduction
    const highReduction = substitutes.find(s => s.injuryRiskReduction === 'high');
    if (highReduction) selected = highReduction;
  } else if (injuryRisk === 'medium') {
    // Prefer stimulus maintenance
    const maintained = substitutes.find(s => s.maintainsStimulus);
    if (maintained) selected = maintained;
  }
  
  return {
    originalExercise: exerciseName,
    substituteExercise: selected.substitute,
    reason: selected.reason,
    maintainsStimulus: selected.maintainsStimulus,
    injuryRiskReduction: selected.injuryRiskReduction,
  };
}

/**
 * Check if an exercise has elevated injury risk based on history
 * Returns risk level based on recent performance and soreness patterns
 */
export async function assessExerciseInjuryRisk(
  exerciseName: string
): Promise<{ riskLevel: 'high' | 'medium' | 'low'; reason: string }> {
  // Get exercise history
  const history = await getExerciseHistory(exerciseName);
  
  if (history.length < 3) {
    return { riskLevel: 'low', reason: 'Insufficient data' };
  }
  
  // Get recent recovery data
  const recoveryHistory = await getRecoveryHistory();
  const recentRecovery = recoveryHistory.slice(-7);
  
  // Check for warning signs
  const recentHistory = history.slice(-5);
  
  // Factor 1: High RPE trend
  const avgRPE = recentHistory.reduce((sum, h) => {
    const sets = h.sets || [];
    const rpes = sets.filter(s => s.rpe && s.rpe > 0).map(s => s.rpe!);
    return sum + (rpes.length > 0 ? rpes.reduce((a, b) => a + b, 0) / rpes.length : 7);
  }, 0) / recentHistory.length;
  
  if (avgRPE >= 9) {
    return { riskLevel: 'high', reason: 'Consistently high RPE (9+)' };
  }
  
  // Factor 2: High soreness related to exercise muscle group
  const avgSoreness = recentRecovery.length > 0
    ? recentRecovery.reduce((sum, r) => sum + r.overallSoreness, 0) / recentRecovery.length
    : 3;
  
  if (avgSoreness >= 7) {
    return { riskLevel: 'high', reason: 'Elevated soreness levels' };
  }
  
  // Factor 3: Failed reps
  const failedSets = recentHistory.filter(h => {
    const sets = h.sets || [];
    // Parse target reps from targetSets (e.g., "3x10" -> 10)
    const targetSetsStr = h.targetSets || '';
    const repsMatch = targetSetsStr.match(/\d+\s*[x×]\s*(\d+)/i);
    const targetReps = repsMatch ? parseInt(repsMatch[1]) : 10;
    return sets.some(s => s.reps < targetReps * 0.8);
  });
  
  if (failedSets.length >= 2) {
    return { riskLevel: 'medium', reason: 'Multiple failed sets recently' };
  }
  
  // Factor 4: RPE trending up rapidly
  if (recentHistory.length >= 3) {
    const rpeValues = recentHistory.map(h => {
      const sets = h.sets || [];
      const rpes = sets.filter(s => s.rpe && s.rpe > 0).map(s => s.rpe!);
      return rpes.length > 0 ? rpes.reduce((a, b) => a + b, 0) / rpes.length : 7;
    });
    
    const firstHalf = rpeValues.slice(0, Math.floor(rpeValues.length / 2));
    const secondHalf = rpeValues.slice(Math.floor(rpeValues.length / 2));
    const trend = (secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length) -
                  (firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length);
    
    if (trend > 1) {
      return { riskLevel: 'medium', reason: 'RPE increasing rapidly' };
    }
  }
  
  return { riskLevel: 'low', reason: 'No concerning patterns' };
}

/**
 * Get substitution recommendation for workout
 * Returns list of exercises that should be substituted
 */
export async function getWorkoutSubstitutions(
  exercises: Array<{ name: string; targetLoad?: number }>
): Promise<SubstitutionResult[]> {
  const substitutions: SubstitutionResult[] = [];
  
  for (const exercise of exercises) {
    const risk = await assessExerciseInjuryRisk(exercise.name);
    
    if (risk.riskLevel === 'high' || risk.riskLevel === 'medium') {
      const substitute = getExerciseSubstitute(exercise.name, risk.riskLevel);
      
      if (substitute) {
        substitutions.push(substitute);
        console.log(` Substitution: ${exercise.name} → ${substitute.substituteExercise} (${risk.reason})`);
      }
    }
  }
  
  return substitutions;
}

// ============================================================================
// SMART SUBSTITUTION RECOMMENDATIONS
// ============================================================================

export interface SmartSubstitute {
  exercise: Exercise;
  matchScore: number;
  matchReasons: string[];
}

export interface SmartSubstituteOptions {
  limit?: number;
  /** Equipment chip filter — keep only candidates doable with this equipment. */
  equipment?: EquipmentFilter | null;
  /** Injury: exclude candidates whose PRIMARY muscles include any of these. */
  avoidMuscles?: string[];
  /** Injury: exclude candidates whose name contains any of these keywords. */
  avoidExerciseNames?: string[];
}

/** The equipment chips the swap/search UI offers. */
export type EquipmentFilter = 'bodyweight' | 'dumbbell' | 'barbell' | 'kettlebell' | 'machine' | 'cable' | 'band';
export const EQUIPMENT_FILTERS: EquipmentFilter[] = ['bodyweight', 'dumbbell', 'barbell', 'kettlebell', 'machine', 'cable', 'band'];

const EQUIPMENT_MATCHERS: Record<EquipmentFilter, (equip: string[]) => boolean> = {
  bodyweight: e => e.length === 0 || e.some(x => /\bnone\b|body|bodyweight/.test(x)),
  dumbbell: e => e.some(x => x.includes('dumbbell')),
  barbell: e => e.some(x => x.includes('barbell')),
  kettlebell: e => e.some(x => x.includes('kettlebell')),
  machine: e => e.some(x => /machine|leverage|smith|sled|lever/.test(x)),
  cable: e => e.some(x => x.includes('cable')),
  band: e => e.some(x => x.includes('band')),
};

/** True when an exercise is doable with the selected equipment chip. */
export function matchesEquipmentFilter(ex: Exercise, filter: EquipmentFilter): boolean {
  return EQUIPMENT_MATCHERS[filter]((ex.equipment ?? []).map(s => s.toLowerCase()));
}

/** True when an exercise should be hidden for the given injury avoidance. */
export function hitsAvoidance(ex: Exercise, avoidMuscles: string[], avoidExerciseNames: string[]): boolean {
  const nameLower = ex.name.toLowerCase();
  if (avoidExerciseNames.some(a => nameLower.includes(a.toLowerCase()))) return true;
  const avoidSet = new Set(avoidMuscles.map(m => m.toLowerCase()));
  return ex.primaryMuscles.some(m => avoidSet.has(m.toLowerCase()));
}

/** Turn a set of injury locations into the muscles + exercise keywords to avoid,
 *  using INJURY_AVOID_MAP. Used by the swap sheet to filter recommendations. */
export function getInjuryAvoidance(injuryLocations: string[]): { muscles: string[]; exerciseNames: string[] } {
  const muscles = new Set<string>();
  const names = new Set<string>();
  try {
    // Deliberate require(): lazy, inside a try. NOTE this is also why
    // getSubstituteForInjury returns null for every input under vitest's ESM
    // loader — see lib/__tests__/trackingTypeIntegrity.test.ts.
    const { INJURY_AVOID_MAP } = require('./types/trainingBreak');
    for (const loc of injuryLocations) {
      const info = INJURY_AVOID_MAP[loc];
      if (!info) continue;
      (info.muscleGroups ?? []).forEach((m: string) => muscles.add(m));
      (info.exercises ?? []).forEach((e: string) => names.add(e));
    }
  } catch { /* map unavailable */ }
  return { muscles: Array.from(muscles), exerciseNames: Array.from(names) };
}

/**
 * Get smart substitution recommendations for an exercise.
 * Ranks by muscle overlap, movement pattern (force+mechanic), tracking, and
 * like-for-like equipment. Optionally filters by an equipment chip and by injury
 * avoidance (muscles / exercise keywords).
 *
 * Back-compat: a number second arg is treated as `limit`.
 */
export function getSmartSubstitutes(
  exerciseName: string,
  optionsOrLimit?: number | SmartSubstituteOptions
): SmartSubstitute[] {
  const opts: SmartSubstituteOptions = typeof optionsOrLimit === 'number' ? { limit: optionsOrLimit } : (optionsOrLimit ?? {});
  const { limit = 6, equipment = null, avoidMuscles = [], avoidExerciseNames = [] } = opts;

  // findExercise (canonicalization + alias + fuzzy) so aliased/variant program
  // names still resolve — a raw name match returned zero for many lifts.
  const currentExercise = findExercise(exerciseName);
  if (!currentExercise) return [];

  const currentEquip = new Set((currentExercise.equipment ?? []).map(s => s.toLowerCase()));
  const scored: SmartSubstitute[] = [];

  for (const candidate of ALL_EXERCISES) {
    if (candidate.id === currentExercise.id) continue;
    // Equipment chip filter (hard) + injury exclusions (hard).
    if (equipment && !matchesEquipmentFilter(candidate, equipment)) continue;
    if ((avoidMuscles.length || avoidExerciseNames.length) && hitsAvoidance(candidate, avoidMuscles, avoidExerciseNames)) continue;

    let score = 0;
    const reasons: string[] = [];

    // 1. Primary muscle overlap (most important) — up to 40
    const primaryOverlap = currentExercise.primaryMuscles.filter(m => candidate.primaryMuscles.includes(m));
    if (primaryOverlap.length > 0) {
      score += Math.min(40, primaryOverlap.length * 20);
      reasons.push(`Same muscles: ${primaryOverlap.join(', ')}`);
    }

    // 2. Tracking type — 25 / 10
    if (candidate.trackingType === currentExercise.trackingType) {
      score += 25;
      reasons.push('Same tracking type');
    } else if (isCompatibleTrackingType(currentExercise.trackingType, candidate.trackingType)) {
      score += 10;
      reasons.push('Compatible tracking');
    }

    // 3. Movement pattern — force (15) + mechanic (10), with a +10 bonus when
    //    BOTH match so a true like-for-like pattern outranks a loose muscle match.
    const forceMatch = !!candidate.force && candidate.force === currentExercise.force;
    const mechMatch = !!candidate.mechanic && candidate.mechanic === currentExercise.mechanic;
    if (forceMatch) score += 15;
    if (mechMatch) score += 10;
    if (forceMatch && mechMatch) { score += 10; reasons.push('Same movement pattern'); }
    else if (forceMatch) reasons.push(`${candidate.force} movement`);
    else if (mechMatch) reasons.push(candidate.mechanic === 'compound' ? 'Compound' : 'Isolation');

    // 4. Like-for-like equipment nudge — only when not chip-filtering (with a
    //    chip active the whole list already shares that equipment).
    if (!equipment && currentEquip.size > 0) {
      if ((candidate.equipment ?? []).some(e => currentEquip.has(e.toLowerCase()))) {
        score += 8;
        reasons.push('Same equipment');
      }
    }

    // 5. Category (5) + secondary muscle overlap (up to 5)
    if (candidate.category === currentExercise.category) score += 5;
    const secondaryOverlap = currentExercise.secondaryMuscles.filter(m =>
      candidate.secondaryMuscles.includes(m) || candidate.primaryMuscles.includes(m)
    );
    if (secondaryOverlap.length > 0) score += Math.min(5, secondaryOverlap.length * 2);

    if (score >= 20 && primaryOverlap.length > 0) {
      scored.push({ exercise: candidate, matchScore: score, matchReasons: reasons });
    }
  }

  return scored.sort((a, b) => b.matchScore - a.matchScore).slice(0, limit);
}

/**
 * Check if two tracking types are compatible for substitution
 */
function isCompatibleTrackingType(original: TrackingType, substitute: TrackingType): boolean {
  // Weight-based exercises can substitute for each other
  const weightBased: TrackingType[] = ['weight-reps', 'bodyweight-reps'];
  if (weightBased.includes(original) && weightBased.includes(substitute)) {
    return true;
  }

  // Cardio types can substitute for each other
  const cardioBased: TrackingType[] = ['distance-time', 'time', 'interval'];
  if (cardioBased.includes(original) && cardioBased.includes(substitute)) {
    return true;
  }

  return false;
}

/**
 * Get substitutes by specific criteria
 */
export function getSubstitutesByMuscle(
  muscleGroup: MuscleGroup,
  trackingType?: TrackingType,
  limit: number = 10
): Exercise[] {
  return ALL_EXERCISES
    .filter(ex => {
      if (!ex.primaryMuscles.includes(muscleGroup)) return false;
      if (trackingType && ex.trackingType !== trackingType) return false;
      return true;
    })
    .slice(0, limit);
}

// ============================================================================
// INJURY-AWARE SUBSTITUTION
// ============================================================================

/**
 * Get a substitute exercise for an avoided exercise based on injury location.
 * Uses INJURY_AVOID_MAP from trainingBreak types, then falls back to smart substitutes.
 */
export function getSubstituteForInjury(
  exerciseName: string,
  injuryLocation: string
): SubstitutionResult | null {
  try {
    const { INJURY_AVOID_MAP } = require('./types/trainingBreak');
    const avoidInfo = INJURY_AVOID_MAP[injuryLocation];
    if (!avoidInfo) return null;

    // Check if this exercise has a direct substitute mapping
    const normalized = exerciseName.toLowerCase();
    for (const [avoid, sub] of Object.entries(avoidInfo.substitutes)) {
      if (normalized.includes(avoid.toLowerCase())) {
        return {
          originalExercise: exerciseName,
          substituteExercise: sub as string,
          reason: `Injury protection (${injuryLocation})`,
          maintainsStimulus: true,
          injuryRiskReduction: 'high',
        };
      }
    }

    // Fall back to smart substitutes, filtering out avoided exercises
    const avoidSet = new Set(avoidInfo.exercises.map((e: string) => e.toLowerCase()));
    const avoidMuscles = new Set(avoidInfo.muscleGroups);

    const smartSubs = getSmartSubstitutes(exerciseName, 10)
      .filter(sub => !avoidSet.has(sub.exercise.name.toLowerCase()))
      .filter(sub => !sub.exercise.primaryMuscles.some(m => avoidMuscles.has(m)));

    if (smartSubs.length > 0) {
      return {
        originalExercise: exerciseName,
        substituteExercise: smartSubs[0].exercise.name,
        reason: `Injury protection (${injuryLocation}) — smart match`,
        maintainsStimulus: smartSubs[0].matchScore > 40,
        injuryRiskReduction: 'medium',
      };
    }
  } catch (e) {
    // Module not available
  }

  return null;
}

